import { Attachment } from '@vnetwork/datacentral-core/domain'
import {
    createContext,
    lazy,
    Suspense,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    type MouseEvent,
    type DragEvent,
} from 'react'
import {
    FilePreviewDialog,
    detectFileKind,
} from '@vnetwork/datacentral-ui/primitives'
import { PresignedGetUrl } from '@/services/drive-download'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import { t } from 'i18next'
import datacentral from '@/utils/axios/datacentral'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// PDFs are drawn by our own pdf.js viewer (all pages, page nav, zoom), never by
// the design system dialog's `<iframe src>`, which hands them to whatever PDF
// viewer the browser has (none on most phones, first page only on iOS).
// Lazy: keeps pdf.js out of the initial chunk.
const PdfPreviewDialog = lazy(
    () => import('@/components/PDFViewer/PdfPreviewDialog')
)

// Files the Office Online viewer (DS `office` kind) renders. The DS detects
// Office only from the name/URL extension, so files whose name has no
// extension are mapped here from their MIME type (or `extension`).
const OFFICE_EXTENSIONS = new Set([
    'doc',
    'docx',
    'docm',
    'dotx',
    'xls',
    'xlsx',
    'xlsm',
    'xlsb',
    'ppt',
    'pptx',
    'pptm',
    'ppsx',
    'pps',
    'odt',
    'ods',
    'odp',
])
const OFFICE_MIME_TYPES = new Set([
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.document.macroenabled.12',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/vnd.ms-excel.sheet.binary.macroenabled.12',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.ms-powerpoint.presentation.macroenabled.12',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
])

const isOfficeFile = (mimeType?: string, extension?: string) => {
    const mime = mimeType?.split(';')[0].trim().toLowerCase()
    if (mime && OFFICE_MIME_TYPES.has(mime)) return true
    // `extension` is "docx" / ".docx", or a MIME subtype from some callers.
    const ext = extension?.trim().toLowerCase().replace(/^\./, '')
    return (
        !!ext &&
        (OFFICE_EXTENSIONS.has(ext) ||
            OFFICE_MIME_TYPES.has(`application/${ext}`))
    )
}

interface FileItem {
    url?: string
    fileKey?: string
    downloadUrl?: string
    attachmentInfo?: Partial<Attachment>
    manualType?: boolean
    extension?: string
    isHeic?: boolean
    /**
     * Whether the user may save the original. Absent = allowed. `false` is the
     * view-only contract: no download button, no "open in new tab" of the raw
     * URL, no right-click save (see `PreviewOptions`).
     */
    allowDownload?: boolean
    /**
     * For `previewMultiple` when the rule is per file and costs a request
     * (Drive share level): called once, when the file is first shown. Until it
     * settles the file is view-only; a rejection or `false` keeps it view-only
     * (fail closed). Ignored when `allowDownload` is set.
     */
    resolveAllowDownload?: () => Promise<boolean>
    /** Mints a new `url` when the shared one expired (PDF viewer's Retry). */
    refreshUrl?: () => Promise<string | undefined>
    /** See `PreviewOptions.allowPrint`. */
    allowPrint?: boolean
}

/** Per-file options of `preview()`. */
interface PreviewOptions {
    /**
     * `false` = view only: the dialog shows no download button (even when a
     * `downloadUrl` is passed), the PDF viewer drops its "open in new tab" link
     * and text layer, right-click is blocked and video/audio lose the native
     * download control. Callers pass their existing flag here (Asset
     * `can_download`). It is a UI rule: the URL itself can still be fetched.
     */
    allowDownload?: boolean
    /**
     * Presigned links expire in minutes. Give a function that returns a fresh
     * URL for this file and the PDF viewer's Retry uses it instead of the old one.
     */
    refreshUrl?: () => Promise<string | undefined>
    /**
     * Opt-in Print button in the PDF viewer (PDFs only; Drive for now). Never
     * shown while the file is view-only.
     */
    allowPrint?: boolean
}

interface FilePreviewContextProps {
    preview: (
        url: string,
        downloadUrl?: string,
        attachmentInfo?: Partial<Attachment>,
        manualType?: boolean,
        options?: PreviewOptions
    ) => void
    previewMultiple: (files: FileItem[], startIndex?: number) => void
    closePreview: () => void
    goToNext: () => void
    goToPrevious: () => void
    currentIndex: number
    totalFiles: number
    hasNext: boolean
    hasPrevious: boolean
    isLoading: boolean
}

const FilePreviewContext = createContext<FilePreviewContextProps>({
    preview: () => {},
    previewMultiple: () => {},
    closePreview: () => {},
    goToNext: () => {},
    goToPrevious: () => {},
    currentIndex: 0,
    totalFiles: 0,
    hasNext: false,
    hasPrevious: false,
    isLoading: false,
})

const FilePreviewContextProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [files, setFiles] = useState<FileItem[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const heicUrlsRef = useRef<Map<string, string>>(new Map())
    const queryClient = useQueryClient()
    // Latest list for resolveAccess (called outside a render) and a generation
    // so a late answer for a closed / replaced list is dropped.
    const filesRef = useRef<FileItem[]>([])
    const accessGeneration = useRef(0)
    const accessRequested = useRef<Set<number>>(new Set())

    const currentFile = files[currentIndex]
    const totalFiles = files.length
    const hasNext = currentIndex < totalFiles - 1
    const hasPrevious = currentIndex > 0

    const generatePresignedUrl = useCallback(
        async (fileItem: FileItem): Promise<string> => {
            if (fileItem.url) return fileItem.url
            if (!fileItem.fileKey) throw new Error('No file key provided')

            try {
                setIsLoading(true)
                const response = await PresignedGetUrl(fileItem.fileKey)
                return response.result as string
            } catch (error) {
                console.error('Failed to generate presigned URL:', error)
                toast.error(t('try-again'))
                throw error
            } finally {
                setIsLoading(false)
            }
        },
        []
    )

    const updateFileWithUrl = useCallback(
        async (index: number) => {
            setFiles((prevFiles) => {
                if (index < 0 || index >= prevFiles.length) return prevFiles

                const fileItem = prevFiles[index]
                if (fileItem.url) return prevFiles

                // Handle HEIC files differently
                if (fileItem.isHeic && fileItem.fileKey) {
                    setIsLoading(true)

                    const fetchHeicPreview = async () => {
                        // Try to get from cache first
                        const cachedData = queryClient.getQueryData<Blob>([
                            QUERY_KEYS.HEIC_PREVIEW,
                            fileItem.fileKey,
                        ])

                        if (cachedData) {
                            // Use cached data
                            return cachedData
                        } else {
                            // Fetch and cache if not present
                            return queryClient.fetchQuery({
                                queryKey: [
                                    QUERY_KEYS.HEIC_PREVIEW,
                                    fileItem.fileKey,
                                ],
                                queryFn: async () => {
                                    const response = await datacentral.get(
                                        '/drive/heic-preview',
                                        {
                                            params: { path: fileItem.fileKey },
                                            responseType: 'blob',
                                        }
                                    )
                                    return response.data as Blob
                                },
                                staleTime: 5 * 60 * 1000, // 5 minutes
                            })
                        }
                    }

                    // Fetch both the converted preview and original URL concurrently
                    Promise.all([
                        fetchHeicPreview(),
                        PresignedGetUrl(fileItem.fileKey),
                    ])
                        .then(([blob, presignedResponse]) => {
                            const previewUrl = URL.createObjectURL(blob)
                            const downloadUrl = presignedResponse.result

                            // Store the preview URL for cleanup
                            if (fileItem.fileKey) {
                                const oldUrl = heicUrlsRef.current.get(
                                    fileItem.fileKey
                                )
                                if (oldUrl) {
                                    URL.revokeObjectURL(oldUrl)
                                }
                                heicUrlsRef.current.set(
                                    fileItem.fileKey,
                                    previewUrl
                                )
                            }

                            setFiles((currentFiles) =>
                                currentFiles.map((file, i) =>
                                    i === index
                                        ? {
                                              ...file,
                                              url: previewUrl,
                                              downloadUrl: downloadUrl,
                                              attachmentInfo: {
                                                  ...file.attachmentInfo,
                                                  attachment_file_url:
                                                      previewUrl,
                                                  attachment_file_type:
                                                      'image/jpeg',
                                              },
                                          }
                                        : file
                                )
                            )
                            setIsLoading(false)
                        })
                        .catch((error) => {
                            console.error('Failed to load HEIC preview:', error)
                            toast.error(t('Failed to load HEIC preview'))
                            setIsLoading(false)
                        })
                } else {
                    // Handle non-HEIC files (existing logic)
                    generatePresignedUrl(fileItem)
                        .then((url) => {
                            setFiles((currentFiles) =>
                                currentFiles.map((file, i) =>
                                    i === index
                                        ? {
                                              ...file,
                                              url,
                                              downloadUrl: url, // Same URL for both
                                              attachmentInfo: {
                                                  ...file.attachmentInfo,
                                                  attachment_file_url: url,
                                              },
                                          }
                                        : file
                                )
                            )
                        })
                        .catch((error) =>
                            console.error(
                                'Failed to update file with URL:',
                                error
                            )
                        )
                }

                return prevFiles
            })
        },
        [generatePresignedUrl]
    )

    // Retry in the PDF viewer: presigned links live minutes, so mint a new one
    // (files that came with a ready `url` and no key cannot be re-resolved).
    const refreshUrl = useCallback(
        (index: number) => {
            const file = filesRef.current[index] ?? files[index]
            if (!file || file.isHeic) return
            if (file.refreshUrl) {
                const generation = accessGeneration.current
                file.refreshUrl()
                    .then((fresh) => {
                        if (!fresh || generation !== accessGeneration.current)
                            return
                        setFiles((current) =>
                            current.map((item, i) =>
                                i === index
                                    ? {
                                          ...item,
                                          url: fresh,
                                          // View-only never gets a save URL.
                                          downloadUrl:
                                              item.allowDownload === false
                                                  ? undefined
                                                  : fresh,
                                      }
                                    : item
                            )
                        )
                    })
                    .catch((error) =>
                        console.error('Failed to refresh file URL:', error)
                    )
                return
            }
            if (!file.fileKey) return
            setFiles((current) =>
                current.map((item, i) =>
                    i === index ? { ...item, url: undefined } : item
                )
            )
            updateFileWithUrl(index)
        },
        [files, updateFileWithUrl]
    )

    // Per-file download rule that needs a request (`resolveAllowDownload`):
    // asked once per file, when it is first shown.
    const resolveAccess = useCallback((index: number) => {
        const file = filesRef.current[index]
        if (!file?.resolveAllowDownload || accessRequested.current.has(index))
            return
        accessRequested.current.add(index)
        const generation = accessGeneration.current
        file.resolveAllowDownload()
            .catch(() => false)
            .then((allowed) => {
                if (generation !== accessGeneration.current) return
                setFiles((current) =>
                    current.map((item, i) =>
                        i === index ? { ...item, allowDownload: allowed } : item
                    )
                )
            })
    }, [])

    const preview = useCallback(
        (
            url: string,
            downloadUrl?: string,
            attachmentInfo?: Partial<Attachment>,
            manualType?: boolean,
            options?: PreviewOptions
        ) => {
            accessGeneration.current += 1
            filesRef.current = []
            setFiles([
                {
                    url,
                    // View-only: never keep a save URL around for this file.
                    downloadUrl:
                        options?.allowDownload === false
                            ? undefined
                            : downloadUrl,
                    attachmentInfo,
                    manualType: manualType || false,
                    allowDownload: options?.allowDownload,
                    refreshUrl: options?.refreshUrl,
                    allowPrint: options?.allowPrint,
                },
            ])
            setCurrentIndex(0)
            setOpen(true)
        },
        []
    )

    const previewMultiple = useCallback(
        (fileItems: FileItem[], startIndex = 0) => {
            const startIdx = Math.min(startIndex, fileItems.length - 1)
            // A file whose rule is still to be resolved is view-only until then.
            const initial = fileItems.map((item) =>
                item.resolveAllowDownload && item.allowDownload === undefined
                    ? { ...item, allowDownload: false }
                    : item
            )
            accessGeneration.current += 1
            accessRequested.current = new Set()
            filesRef.current = initial
            setFiles(initial)
            setCurrentIndex(startIdx)
            setOpen(true)

            if (fileItems.length > 0) {
                setTimeout(() => {
                    updateFileWithUrl(startIdx)
                    resolveAccess(startIdx)
                }, 0)

                // Pre-fetch HEIC previews for nearby files
                fileItems.forEach((item, idx) => {
                    if (
                        item.isHeic &&
                        item.fileKey &&
                        Math.abs(idx - startIdx) <= 2
                    ) {
                        // Pre-fetch HEIC files within 2 positions of current
                        queryClient.prefetchQuery({
                            queryKey: [QUERY_KEYS.HEIC_PREVIEW, item.fileKey],
                            queryFn: async () => {
                                const response = await datacentral.get(
                                    '/drive/heic-preview',
                                    {
                                        params: { path: item.fileKey },
                                        responseType: 'blob',
                                    }
                                )
                                return response.data as Blob
                            },
                            staleTime: 5 * 60 * 1000, // 5 minutes
                        })
                    }
                })
            }
        },
        [updateFileWithUrl, resolveAccess, queryClient]
    )

    const closePreview = useCallback(() => {
        setOpen(false)
        accessGeneration.current += 1
        filesRef.current = []
        setFiles([])
        setCurrentIndex(0)
        setIsLoading(false)

        // Cleanup all HEIC preview URLs
        heicUrlsRef.current.forEach((url) => {
            URL.revokeObjectURL(url)
        })
        heicUrlsRef.current.clear()
    }, [])

    const goToNext = useCallback(() => {
        if (hasNext) {
            const nextIndex = currentIndex + 1
            setCurrentIndex(nextIndex)
            updateFileWithUrl(nextIndex)
            resolveAccess(nextIndex)

            // Pre-fetch next HEIC file if exists
            const nextNextIndex = nextIndex + 1
            if (nextNextIndex < files.length) {
                const nextFile = files[nextNextIndex]
                if (nextFile.isHeic && nextFile.fileKey) {
                    queryClient.prefetchQuery({
                        queryKey: [QUERY_KEYS.HEIC_PREVIEW, nextFile.fileKey],
                        queryFn: async () => {
                            const response = await datacentral.get(
                                '/drive/heic-preview',
                                {
                                    params: { path: nextFile.fileKey },
                                    responseType: 'blob',
                                }
                            )
                            return response.data as Blob
                        },
                        staleTime: 5 * 60 * 1000,
                    })
                }
            }
        }
    }, [
        hasNext,
        currentIndex,
        updateFileWithUrl,
        resolveAccess,
        files,
        queryClient,
    ])

    const goToPrevious = useCallback(() => {
        if (hasPrevious) {
            const prevIndex = currentIndex - 1
            setCurrentIndex(prevIndex)
            updateFileWithUrl(prevIndex)
            resolveAccess(prevIndex)

            // Pre-fetch previous HEIC file if exists
            const prevPrevIndex = prevIndex - 1
            if (prevPrevIndex >= 0) {
                const prevFile = files[prevPrevIndex]
                if (prevFile.isHeic && prevFile.fileKey) {
                    queryClient.prefetchQuery({
                        queryKey: [QUERY_KEYS.HEIC_PREVIEW, prevFile.fileKey],
                        queryFn: async () => {
                            const response = await datacentral.get(
                                '/drive/heic-preview',
                                {
                                    params: { path: prevFile.fileKey },
                                    responseType: 'blob',
                                }
                            )
                            return response.data as Blob
                        },
                        staleTime: 5 * 60 * 1000,
                    })
                }
            }
        }
    }, [
        hasPrevious,
        currentIndex,
        updateFileWithUrl,
        resolveAccess,
        files,
        queryClient,
    ])

    // Cleanup HEIC URLs on unmount
    useEffect(() => {
        return () => {
            heicUrlsRef.current.forEach((url) => {
                URL.revokeObjectURL(url)
            })
            heicUrlsRef.current.clear()
        }
    }, [])

    const previewFile = {
        url: currentFile?.url ?? '',
        name: currentFile?.attachmentInfo?.attachment_file_name,
        mimeType: currentFile?.attachmentInfo?.attachment_file_type,
        // Kind comes from the MIME type, then the file name extension
        // (detectFileKind). Only HEIC previews are forced: they are converted
        // JPEG blobs whose name still ends in .heic. (`manualType` is passed by
        // most callers and must not force 'image', or PDF/Office break.) Office
        // files are forced too: the DS maps them by extension only (names may
        // have none).
        kind: currentFile?.isHeic
            ? ('image' as const)
            : isOfficeFile(
                    currentFile?.attachmentInfo?.attachment_file_type,
                    currentFile?.extension
                )
              ? ('office' as const)
              : undefined,
    }
    // Decided before the URL is resolved too (name / MIME), so the PDF dialog
    // opens straight away instead of flashing the design system one.
    const isPdf = !!currentFile && detectFileKind(previewFile) === 'pdf'
    // One decision for both dialogs: absent flag = allowed; `false` = view only.
    const viewOnly = currentFile?.allowDownload === false
    const onDownload =
        !viewOnly && currentFile?.downloadUrl
            ? () =>
                  downloadFile(
                      currentFile.downloadUrl!,
                      currentFile.attachmentInfo?.attachment_file_name
                  )
            : undefined
    const navigation =
        totalFiles > 1
            ? {
                  index: currentIndex,
                  total: totalFiles,
                  onPrevious: goToPrevious,
                  onNext: goToNext,
              }
            : undefined

    return (
        <FilePreviewContext.Provider
            value={{
                preview,
                previewMultiple,
                closePreview,
                goToNext,
                goToPrevious,
                currentIndex,
                totalFiles,
                hasNext,
                hasPrevious,
                isLoading,
            }}
        >
            {children}
            {isPdf ? (
                open && (
                    <Suspense fallback={null}>
                        <PdfPreviewDialog
                            open={open}
                            onOpenChange={(next) => !next && closePreview()}
                            url={
                                isLoading || !currentFile?.url
                                    ? undefined
                                    : currentFile.url
                            }
                            title={
                                currentFile?.attachmentInfo
                                    ?.attachment_file_name
                            }
                            allowDownload={!viewOnly}
                            onDownload={onDownload}
                            printable={currentFile?.allowPrint}
                            onRefreshUrl={() => refreshUrl(currentIndex)}
                            navigation={navigation}
                        />
                    </Suspense>
                )
            ) : (
                <ViewOnlyGuard active={viewOnly}>
                    {(guardRef) => (
                        <FilePreviewDialog
                            ref={guardRef}
                            open={open}
                            onOpenChange={(next) => !next && closePreview()}
                            loading={isLoading || !currentFile?.url}
                            file={currentFile?.url ? previewFile : undefined}
                            onDownload={onDownload}
                            navigation={navigation}
                        />
                    )}
                </ViewOnlyGuard>
            )}
        </FilePreviewContext.Provider>
    )
}

/**
 * View-only wrapper for the design system dialog (it has no such mode): blocks
 * right-click ("Save image as...", "Save video as...") and dragging an image
 * out, and removes the native download control of video / audio. The dialog
 * renders in a portal, but React events still bubble to this element.
 * Inert when `active` is false.
 */
function ViewOnlyGuard({
    active,
    children,
}: {
    active: boolean
    children: (ref?: (node: HTMLDivElement | null) => void) => React.ReactNode
}) {
    const observer = useRef<MutationObserver | null>(null)
    const guardRef = useCallback(
        (node: HTMLDivElement | null) => {
            observer.current?.disconnect()
            observer.current = null
            if (!node || !active) return
            const harden = () => {
                node.querySelectorAll('video, audio').forEach((media) => {
                    media.setAttribute('controlslist', 'nodownload')
                })
                node.querySelectorAll('img').forEach((image) => {
                    image.draggable = false
                })
            }
            harden()
            observer.current = new MutationObserver(harden)
            observer.current.observe(node, { childList: true, subtree: true })
        },
        [active]
    )
    if (!active) return <>{children()}</>
    return (
        <div
            className="contents"
            onContextMenu={(event: MouseEvent) => event.preventDefault()}
            onDragStart={(event: DragEvent) => event.preventDefault()}
        >
            {children(guardRef)}
        </div>
    )
}

/** Save a file through a temporary link (keeps presigned URLs out of history). */
function downloadFile(url: string, name?: string) {
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noreferrer'
    if (name) link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
}

export const useFilePreview = () => useContext(FilePreviewContext)
export default FilePreviewContextProvider
